import React, { useMemo, useCallback } from 'react';
import { useHistory } from 'react-router';
import styled from 'styled-components/macro';
import moment from 'moment';
import { groupBy, findIndex, find } from 'lodash';
import scaleSize from 'utils/scale-size';
import isLiveProgram from 'utils/is-live-program';
import { CONTENT_TYPES } from 'constants/content-types';
import { ChannelType, LiveEventType } from 'store/pages/loaders/loader-types';
import { playerActions } from 'store/player';
import CustomCollection from 'components/CustomCollection';
import BackButton from 'components/BackButton';
import { useDispatch } from 'react-redux';

interface IProgramsView {
  currentChannel: ChannelType;
  epg: LiveEventType[];
  fetchNextPage: () => unknown;
  fetchPreviousPage: () => unknown;
  isFetching?: boolean;
}

const StyledBackButton = styled(BackButton)``;

const EPGProgramsPage: React.FC<IProgramsView> = (props) => {
  const { currentChannel, epg, isFetching, fetchNextPage, fetchPreviousPage } = props;
  const dispatch = useDispatch();
  const history = useHistory();

  const onContentEnter = useCallback(
    (item) => {
      const { isLive } = item;

      if (!isLive) return console.info('To BE IMPLEMENTED!');

      dispatch(playerActions.navigateToPlayer({ item: currentChannel, useReplace: true }));
    },
    [currentChannel, dispatch],
  );

  const onContentFocus = useCallback(
    (item) => {
      const currentStartTime = moment(item.start_time);

      const hasNoDaysBefore = !find(epg, (program) => {
        return moment(program.start_time).isBefore(currentStartTime, 'day');
      });
      const hasNoDaysAfter = !find(epg, (program) => {
        return moment(program.start_time).isAfter(currentStartTime, 'day');
      });

      if (isFetching) return;

      if (hasNoDaysBefore) {
        fetchPreviousPage();
      }
      if (hasNoDaysAfter) {
        fetchNextPage();
      }
    },
    [epg, isFetching, fetchPreviousPage, fetchNextPage],
  );

  const collections = useMemo(() => {
    const byDays = groupBy(epg, (program) => moment(program.start_time).format('YYYYMMDD'));

    return Object.keys(byDays)
      .sort()
      .map((date) => {
        const data = byDays[date].map((program) => {
          const isLive = isLiveProgram(program);

          return { ...program, currentChannel, isLive };
        });
        const isToday = moment(date).day() === moment().day();

        const title = moment(date)
          .locale('en')
          .format(`${isToday ? '[Today]' : 'dddd'}, MMMM Do`);

        const contentType = CONTENT_TYPES.PROGRAM;

        return { id: date, title, data, contentType };
      });
  }, [currentChannel, epg]);

  const focusedElementIndex = useMemo(() => {
    for (const collection of collections) {
      const index = findIndex(collection.data, 'isLive');

      if (index !== -1) return index;
    }
  }, [collections]);

  const focusedCollectionId = useMemo(() => {
    return collections.find((day) => {
      const isHasLiveProgram = !!find(day.data, 'isLive');
      return isHasLiveProgram;
    })?.id;
  }, [collections]);

  return (
    <div>
      <StyledBackButton onEnter={history.goBack} />
      <CustomCollection
        key={currentChannel}
        id="epg-collection-list"
        collections={collections}
        onContentEnter={onContentEnter}
        onContentFocus={onContentFocus}
        top={scaleSize(170)}
        left={scaleSize(80)}
        focusedCollectionId={focusedCollectionId}
        startListScrollIndex={1}
        elementIndex={focusedElementIndex}
      />
    </div>
  );
};

export default EPGProgramsPage;
